/**
 * DateRangePicker - Date range selection component
 */

import NepaliCalendar from "../calendar/NepaliCalendar.js";
import EnglishCalendar from "../calendar/EnglishCalendar.js";
import NepaliDateConverter from "../converters/NepaliDateConverter.js";

class DateRangePicker {
  constructor(options = {}) {
    this.options = {
      calendar: "nepali",
      language: "en",
      format: "YYYY-MM-DD",
      separator: " - ",
      placeholder: "Select date range",
      startPlaceholder: "Start date",
      endPlaceholder: "End date",
      minDate: null,
      maxDate: null,
      disabledDates: [],
      disabledDaysOfWeek: [],
      predefinedRanges: true,
      singleCalendar: false,
      autoApply: false,
      showInputs: true,
      inline: false,
      onChange: null,
      onShow: null,
      onHide: null,
      ...options,
    };

    this.container = null;
    this.input = null;
    this.pickerContainer = null;
    this.startDate = null;
    this.endDate = null;
    this.isVisible = false;
    this.initialized = false;

    // Add these for debugging
    console.log(
      "DateRangePicker constructor called with options:",
      this.options
    );
  }

  /**
   * Initialize the date range picker
   */
  initialize(element) {
    console.log("DateRangePicker.initialize called with element:", element);

    if (typeof element === "string") {
      element = document.querySelector(element);
    }

    if (!element) {
      throw new Error("Container element not found");
    }

    this.container = element;
    this._createStructure();
    this._attachEventListeners();
    this.initialized = true;

    if (this.options.inline) {
      this.show();
    }
  }

  /**
 * Create picker structure with size support
 * @private
 */
_createStructure() {
  console.log("Creating DateRangePicker structure");

  // Clear container
  this.container.innerHTML = "";
  this.container.classList.add("ndtp-date-range-picker");
  
  // Apply size class
  if (this.options.size) {
    // Remove any existing size classes
    this.container.classList.remove('ndtp-size-sm', 'ndtp-size-md', 'ndtp-size-lg');
    // Add the specified size class
    this.container.classList.add(`ndtp-size-${this.options.size}`);
  }

  // Create input
  if (this.options.showInputs) {
    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.className = "ndtp-input";
    this.input.placeholder = this.options.placeholder;
    this.input.readOnly = true;
    this.container.appendChild(this.input);
  }

  // Create picker container
  this.pickerContainer = document.createElement("div");
  this.pickerContainer.className = "ndtp-picker-container";
  if (!this.options.inline) {
    this.pickerContainer.classList.add("ndtp-popup");
    this.pickerContainer.style.display = "none";
  }
  
  // Also apply size class to picker container for consistency
  if (this.options.size) {
    this.pickerContainer.classList.add(`ndtp-size-${this.options.size}`);
  }

  // Create calendars
  const calendarContainer = document.createElement("div");
  calendarContainer.className = "ndtp-calendar-container";
  calendarContainer.innerHTML = `
    <div class="ndtp-calendar-header">
      <button type="button" class="ndtp-btn ndtp-btn-prev-month">‹</button>
      <div class="ndtp-month-year">
        <select class="ndtp-month-select"></select>
        <select class="ndtp-year-select"></select>
      </div>
      <button type="button" class="ndtp-btn ndtp-btn-next-month">›</button>
    </div>
    <div class="ndtp-days-header">
      <div class="ndtp-day-header">Su</div>
      <div class="ndtp-day-header">Mo</div>
      <div class="ndtp-day-header">Tu</div>
      <div class="ndtp-day-header">We</div>
      <div class="ndtp-day-header">Th</div>
      <div class="ndtp-day-header">Fr</div>
      <div class="ndtp-day-header">Sa</div>
    </div>
    <div class="ndtp-days-grid"></div>
  `;

  this.pickerContainer.appendChild(calendarContainer);

  // Create footer
  if (!this.options.autoApply) {
    const footer = document.createElement("div");
    footer.className = "ndtp-footer";
    footer.innerHTML = `
      <button type="button" class="ndtp-btn ndtp-btn-cancel">Cancel</button>
      <button type="button" class="ndtp-btn ndtp-btn-apply">Apply</button>
    `;
    this.pickerContainer.appendChild(footer);
  }

  this.container.appendChild(this.pickerContainer);

  // Initialize calendar data
  this.currentDate = this._getCurrentDate();
  this.viewDate = { ...this.currentDate };

  // Initial render
  this._renderCalendar();
}

  /**
   * Get current date based on calendar type
   */
  _getCurrentDate() {
    if (this.options.calendar === "nepali") {
      const converter = new NepaliDateConverter();
      return converter.getTodayBS();
    } else {
      const today = new Date();
      return {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate(),
      };
    }
  }

  /**
   * Render calendar
   */
  _renderCalendar() {
    console.log("Rendering calendar with viewDate:", this.viewDate);

    // Update month select
    const monthSelect =
      this.pickerContainer.querySelector(".ndtp-month-select");
    monthSelect.innerHTML = "";
    const monthNames = this._getMonthNames();
    monthNames.forEach((name, index) => {
      const option = document.createElement("option");
      option.value = index + 1;
      option.textContent = name;
      if (index + 1 === this.viewDate.month) {
        option.selected = true;
      }
      monthSelect.appendChild(option);
    });

    // Update year select
    const yearSelect = this.pickerContainer.querySelector(".ndtp-year-select");
    yearSelect.innerHTML = "";
    const currentYear = this.viewDate.year;
    for (let year = currentYear - 50; year <= currentYear + 50; year++) {
      const option = document.createElement("option");
      option.value = year;
      option.textContent = year;
      if (year === currentYear) {
        option.selected = true;
      }
      yearSelect.appendChild(option);
    }

    // Render days grid
    this._renderDaysGrid();
  }

  /**
   * Render days grid
   */
  _renderDaysGrid() {
    const daysGrid = this.pickerContainer.querySelector(".ndtp-days-grid");
    daysGrid.innerHTML = "";

    const year = this.viewDate.year;
    const month = this.viewDate.month;
    const daysInMonth = this._getDaysInMonth(year, month);
    const firstDayOfMonth = this._getFirstDayOfMonth(year, month);

    // Previous month days
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const daysInPrevMonth = this._getDaysInMonth(prevYear, prevMonth);

    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const date = { year: prevYear, month: prevMonth, day };
      const cell = this._createDayCell(date, false);
      daysGrid.appendChild(cell);
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = { year, month, day };
      const cell = this._createDayCell(date, true);
      daysGrid.appendChild(cell);
    }

    // Next month days
    const totalCells = daysGrid.children.length;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);

    if (remainingCells > 0) {
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;

      for (let day = 1; day <= remainingCells; day++) {
        const date = { year: nextYear, month: nextMonth, day };
        const cell = this._createDayCell(date, false);
        daysGrid.appendChild(cell);
      }
    }
  }

  /**
   * Create day cell
   */
  _createDayCell(date, isCurrentMonth) {
    const cell = document.createElement("div");
    cell.className = "ndtp-day";

    if (!isCurrentMonth) {
      cell.classList.add("ndtp-other-month");
    }

    // Check if date is in selected range
    if (this.startDate && this.endDate) {
      if (this._isDateEqual(date, this.startDate)) {
        cell.classList.add("ndtp-range-start");
      }
      if (this._isDateEqual(date, this.endDate)) {
        cell.classList.add("ndtp-range-end");
      }
      if (this._isDateInRange(date, this.startDate, this.endDate)) {
        cell.classList.add("ndtp-in-range");
      }
    } else if (this.startDate && this._isDateEqual(date, this.startDate)) {
      cell.classList.add("ndtp-selected");
    }

    cell.textContent = date.day;
    cell.dataset.date = JSON.stringify(date);

    return cell;
  }

  /**
   * Attach event listeners
   */
  _attachEventListeners() {
    console.log("Attaching event listeners");

    // Input click
    if (this.input) {
      this.input.addEventListener("click", (e) => {
        e.stopPropagation();
        this.show();
      });
    }

    // Calendar navigation
    const prevBtn = this.pickerContainer.querySelector(".ndtp-btn-prev-month");
    const nextBtn = this.pickerContainer.querySelector(".ndtp-btn-next-month");
    const monthSelect =
      this.pickerContainer.querySelector(".ndtp-month-select");
    const yearSelect = this.pickerContainer.querySelector(".ndtp-year-select"); // Fix the typo here

    if (prevBtn) {
      prevBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this._prevMonth();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this._nextMonth();
      });
    }

    if (monthSelect) {
      monthSelect.addEventListener("change", (e) => {
        e.stopPropagation();
        this.viewDate.month = parseInt(e.target.value);
        this._renderCalendar();
      });
    }

    if (yearSelect) {
      yearSelect.addEventListener("change", (e) => {
        e.stopPropagation();
        this.viewDate.year = parseInt(e.target.value);
        this._renderCalendar();
      });
    }

    // Day selection
    this.pickerContainer.addEventListener("click", (e) => {
      e.stopPropagation();
      const dayCell = e.target.closest(".ndtp-day");
      if (dayCell && !dayCell.classList.contains("ndtp-other-month")) {
        const date = JSON.parse(dayCell.dataset.date);
        this._selectDate(date);
      }
    });

    // Apply/Cancel buttons
    if (!this.options.autoApply) {
      const cancelBtn = this.pickerContainer.querySelector(".ndtp-btn-cancel");
      const applyBtn = this.pickerContainer.querySelector(".ndtp-btn-apply");

      if (cancelBtn) {
        cancelBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.hide();
        });
      }

      if (applyBtn) {
        applyBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this._applySelection();
        });
      }
    }

    // Close on outside click
    document.addEventListener("click", (e) => {
      if (this.isVisible && !this.container.contains(e.target)) {
        this.hide();
      }
    });
  }

  /**
   * Select date
   */
  _selectDate(date) {
    console.log("Date selected:", date);

    if (!this.startDate || (this.startDate && this.endDate)) {
      // Start new selection
      this.startDate = date;
      this.endDate = null;
    } else {
      // Complete selection
      if (this._compareDates(date, this.startDate) < 0) {
        this.endDate = this.startDate;
        this.startDate = date;
      } else {
        this.endDate = date;
      }

      if (this.options.autoApply) {
        this._applySelection();
      }
    }

    this._updateInput();
    this._renderDaysGrid();
  }

  /**
   * Apply selection
   */
  _applySelection() {
    if (this.startDate && this.endDate) {
      if (this.options.onChange) {
        this.options.onChange({
          start: this.startDate,
          end: this.endDate,
        });
      }
      this.hide();
    }
  }

  /**
   * Update input
   */
  _updateInput() {
    if (!this.input) return;

    if (this.startDate && this.endDate) {
      const startStr = this._formatDate(this.startDate);
      const endStr = this._formatDate(this.endDate);
      this.input.value = `${startStr}${this.options.separator}${endStr}`;
    } else if (this.startDate) {
      this.input.value = this._formatDate(this.startDate);
    } else {
      this.input.value = "";
    }
  }

  /**
   * Format date
   */
  _formatDate(date) {
    const year = date.year.toString().padStart(4, "0");
    const month = date.month.toString().padStart(2, "0");
    const day = date.day.toString().padStart(2, "0");

    return this.options.format
      .replace("YYYY", year)
      .replace("MM", month)
      .replace("DD", day);
  }

  /**
   * Show picker
   */
  show() {
    if (this.isVisible || this.options.inline) return;

    this.pickerContainer.style.display = "block";
    this.isVisible = true;

    if (this.options.onShow) {
      this.options.onShow();
    }
  }

  /**
   * Hide picker
   */
  hide() {
    if (!this.isVisible || this.options.inline) return;

    this.pickerContainer.style.display = "none";
    this.isVisible = false;

    if (this.options.onHide) {
      this.options.onHide();
    }
  }

  /**
   * Get range
   */
  getRange() {
    if (!this.startDate || !this.endDate) {
      return null;
    }

    return {
      start: { ...this.startDate },
      end: { ...this.endDate },
    };
  }

  /**
   * Set range
   */
  setRange(range) {
    if (!range || !range.start || !range.end) {
      throw new Error("Invalid range");
    }

    this.startDate = { ...range.start };
    this.endDate = { ...range.end };

    this._updateInput();
    this._renderDaysGrid();
  }

  /**
   * Clear selection
   */
  clear() {
    this.startDate = null;
    this.endDate = null;
    this._updateInput();
    this._renderDaysGrid();
  }

  /**
   * Navigate to previous month
   */
  _prevMonth() {
    this.viewDate.month--;
    if (this.viewDate.month < 1) {
      this.viewDate.month = 12;
      this.viewDate.year--;
    }
    this._renderCalendar();
  }

  /**
   * Navigate to next month
   */
  _nextMonth() {
    this.viewDate.month++;
    if (this.viewDate.month > 12) {
      this.viewDate.month = 1;
      this.viewDate.year++;
    }
    this._renderCalendar();
  }

  /**
   * Get month names
   */
  _getMonthNames() {
    if (this.options.calendar === "nepali") {
      if (this.options.language === "ne") {
        return [
          "बैशाख",
          "जेठ",
          "असार",
          "श्रावण",
          "भदौ",
          "आश्विन",
          "कार्तिक",
          "मंसिर",
          "पौष",
          "माघ",
          "फाल्गुन",
          "चैत",
        ];
      } else {
        return [
          "Baisakh",
          "Jestha",
          "Ashar",
          "Shrawan",
          "Bhadra",
          "Ashoj",
          "Kartik",
          "Mangsir",
          "Poush",
          "Magh",
          "Falgun",
          "Chaitra",
        ];
      }
    } else {
      return [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
    }
  }

  /**
   * Get days in month
   */
  _getDaysInMonth(year, month) {
    if (this.options.calendar === "nepali") {
      const converter = new NepaliDateConverter();
      return converter.getDaysInMonth(year, month);
    } else {
      return new Date(year, month, 0).getDate();
    }
  }

  /**
   * Get first day of month
   */
  _getFirstDayOfMonth(year, month) {
    if (this.options.calendar === "nepali") {
      const converter = new NepaliDateConverter();
      const bsDate = { year, month, day: 1 };
      const adDate = converter.bsToAd(bsDate);
      return new Date(adDate.year, adDate.month - 1, adDate.day).getDay();
    } else {
      return new Date(year, month - 1, 1).getDay();
    }
  }

  /**
   * Compare dates
   */
  _compareDates(date1, date2) {
    if (date1.year !== date2.year) {
      return date1.year - date2.year;
    }
    if (date1.month !== date2.month) {
      return date1.month - date2.month;
    }
    return date1.day - date2.day;
  }

  /**
   * Check if dates are equal
   */
  _isDateEqual(date1, date2) {
    return (
      date1.year === date2.year &&
      date1.month === date2.month &&
      date1.day === date2.day
    );
  }

  /**
   * Check if date is in range
   */
  _isDateInRange(date, start, end) {
    return (
      this._compareDates(date, start) >= 0 && this._compareDates(date, end) <= 0
    );
  }
}

export default DateRangePicker;
